import {
  AllProjectsDocument,
  Project,
  ProjectsDocument,
  RecentProjectsDocument,
  useArchiveProjectMutation,
} from "@gql/sites-api/graphql";
import { ContextMenu, MenuItemProps, MenuItemType, leadElementIconColor } from "react-migration/components/ContextMenu";
import { LandTechEndpoints, routedClient } from "react-migration/lib/persistence/apollo";
import { ProjectEventName, logEventV2 } from "react-migration/lib/util/logEventV2";
import { useCallback, useMemo } from "react";
import clsx from "clsx";

import { ApolloError } from "@apollo/client";
import { Icon } from "react-migration/components/Icon";
import { showError } from "src/js/stores/user/actions/addNotification";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { setEditProjectModal } from "src/js/stores/navigation/actions";

export type ActionsMenuProps = {
  project: Pick<Project, "id" | "title" | "developmentType" | "goal" | "image" | "imageUrl">;
  triggerClassName?: string;
  triggerIconClassName?: string;
};

export const ActionsMenu = ({ project, triggerClassName, triggerIconClassName }: ActionsMenuProps) => {
  const { t } = useTranslation();

  const [archiveProject] = useArchiveProjectMutation({
    client: routedClient,
    context: {
      endpoint: LandTechEndpoints.Sites,
    },
    refetchQueries: [AllProjectsDocument, RecentProjectsDocument, ProjectsDocument],
  });

  const handleEdit = useCallback(() => {
    setEditProjectModal(project);
  }, [project]);

  const handleArchive = useCallback(async () => {
    try {
      await archiveProject({
        variables: { id: project.id },
      });
      logEventV2({ name: ProjectEventName.PROJECTS_TABLE_PROJECT_ARCHIVED, properties: { projectId: project.id } });
    } catch (error) {
      console.error("Failed to archive project:", error);

      if (!(error instanceof ApolloError)) {
        return;
      }

      const firstGraphQLError = error.graphQLErrors[0];

      if (
        firstGraphQLError?.extensions?.code === "BAD_USER_INPUT" &&
        firstGraphQLError.extensions?.message_code === "cannot_archive_last_remaining_workspace"
      ) {
        showError({
          id: "cannot_archive_last_remaining_workspace",
          title: t("projects.error.cannot_archive_last_remaining_workspace.title"),
          description: t("projects.error.cannot_archive_last_remaining_workspace.description"),
          duration: 3000,
        });
      }
    }
  }, [archiveProject, project.id, t]);

  const menuItems: MenuItemProps[] = useMemo(
    () => [
      {
        type: MenuItemType.ITEM,
        testId: "edit-project",
        label: t("projects.table.actions.edit"),
        leadElement: <Icon icon="icon-lt-edit-line" extraClasses={leadElementIconColor} />,
        onClick: handleEdit,
      },
      {
        type: MenuItemType.ITEM,
        testId: "archive-project",
        label: t("projects.table.actions.archive"),
        leadElement: <Icon icon="icon-lt-archive-line" extraClasses={leadElementIconColor} />,
        onClick: handleArchive,
      },
    ],
    [t, handleEdit, handleArchive]
  );

  return (
    <ContextMenu
      menuItems={menuItems}
      triggerTestId="actions-menu-trigger"
      triggerIconProps={{
        icon: "icon-lt-more-2-line",
        size: "sm",
        extraClasses: clsx("text-neutral-800", triggerIconClassName),
        cursor: "pointer",
      }}
      triggerClassName={triggerClassName}
    />
  );
};
